import type { State } from '../core/state';
import { now, safeStringify } from '../core/util';
import type { Emit } from './errors';

type Method = 'log' | 'warn' | 'error';

/** console.warn/error (+ optional log) → breadcrumbs; console.error also becomes a `console` event. */
export function installConsoleCollector(state: State, emit: Emit): () => void {
  const methods: Method[] = state.options.captureConsoleLog ? ['log', 'warn', 'error'] : ['warn', 'error'];
  const originals: Partial<Record<Method, (...args: unknown[]) => void>> = {};

  for (const m of methods) {
    const orig = console[m] as (...args: unknown[]) => void;
    originals[m] = orig;
    console[m] = (...args: unknown[]) => {
      try {
        const message = state.redactor.text(args.map((a) => safeStringify(a, 500)).join(' ')).slice(0, 1000);
        const level = m === 'error' ? 'error' : m === 'warn' ? 'warning' : 'info';
        if (!message.startsWith('[FETrace]')) {
          state.breadcrumbs.push({ type: 'console', level, message, at: now() });
          if (m === 'error') emit({ type: 'console', level: 'error', message, url: location.href, occurred_at: now() });
        }
      } catch {
        /* never break the host page */
      }
      orig.apply(console, args);
    };
  }

  return () => {
    for (const m of methods) if (originals[m]) console[m] = originals[m] as never;
  };
}
